import { useAlert, useAuth } from 'App'
import { useEffect, useState, useRef, useCallback } from 'react'
import RecordRTC, { StereoAudioRecorder } from 'recordrtc'
import { VoxistWebSocketClient, TranscriptionResponse } from '../services/api'
import { getDefaultASRLanguage, type ASRLanguage } from '../utils/localeUtils'
import { VoxistAudioSDK, TranscriptionSegment } from '../sdk'

const API_BASE = window.__ENV__?.API_BASE || 'http://localhost:3000'

const requestRecorder = async () => {
  return navigator.mediaDevices.getUserMedia({
    audio: true,
    video: false
  })
}

const useRecorder = (dictationMode: boolean = false) => {
  const [audioURL, setAudioURL] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [recorder, setRecorder] = useState(null)
  const [stream, setStream] = useState(null)
  const [sdkManager, setSdkManager] = useState<{ wsClient: any; streamManager: any } | null>(null)
  const [responseSocketVoxist, setResponseSocketVoxist] = useState(null)
  const [shouldStartRecording, setShouldStartRecording] = useState(false)
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null)
  const [audioChunks, setAudioChunks] = useState<Blob[]>([])
  const [language, setLanguage] = useState<ASRLanguage>(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage') as ASRLanguage
    return savedLanguage || getDefaultASRLanguage()
  })
  const model = 'banafo' // Always use Banafo (Voxist V3)

  // Compute effective punctuation mode based on language and dictation mode
  const punctuationMode: 'Generated' | 'Dictated' =
    dictationMode ? 'Dictated' : 'Generated'

  // Use ref to avoid stale closure in startRecording callback
  const punctuationModeRef = useRef(punctuationMode)
  useEffect(() => {
    punctuationModeRef.current = punctuationMode
  }, [punctuationMode])

  const auth = useAuth()
  const { setAlert } = useAlert()

  const isWebSocketConnected = useCallback(() => {
    return sdkManager?.wsClient?.isWebSocketConnected() || false
  }, [sdkManager])

  useEffect(() => {
    if (sdkManager) {
      // SDK returns single TranscriptionSegment, convert to array for compatibility
      sdkManager.wsClient.onMessage((segment: TranscriptionSegment) => {
        setResponseSocketVoxist([segment])
      })

      sdkManager.wsClient.onError((error: string) => {
        setAlert({ type: 'error', text: error })
      })
    }
  }, [sdkManager, setAlert])

  // Handle starting recording when SDK manager is ready
  useEffect(() => {
    if (sdkManager && shouldStartRecording && !isRecording) {
      setIsRecording(true)
      setShouldStartRecording(false)
    }
  }, [sdkManager, shouldStartRecording, isRecording])

  useEffect(() => {
    if (isRecording && !sdkManager?.streamManager.isCapturing()) {
      // Start recording with SDK's Audio Stream Manager (includes quality enhancement)
      // AND MediaRecorder for local playback
      requestRecorder()
        .then(async (mediaStream) => {
          // SDK handles audio streaming — pass existing stream to avoid double getUserMedia
          if (sdkManager) {
            await sdkManager.streamManager.startCapture(mediaStream)
          }

          // Set up MediaRecorder for local audio recording (playback only)
          const chunks: Blob[] = []
          const recorder = new MediaRecorder(mediaStream, {
            mimeType: 'audio/webm;codecs=opus'
          })

          recorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              chunks.push(event.data)
            }
          }

          recorder.onstop = () => {
            if (chunks.length > 0) {
              const audioBlob = new Blob(chunks, { type: 'audio/webm;codecs=opus' })
              const audioUrl = URL.createObjectURL(audioBlob)
              setAudioURL(audioUrl)
            }
          }

          recorder.start(1000) // Collect data every 1 second

          setStream(mediaStream)
          setMediaRecorder(recorder)
          setAudioChunks(chunks)
        })
        .catch((e) => {
          console.error('Error starting recording:', e)
          setAlert({ type: 'error', text: "Can't get microphone access" })
        })
    } else if (!isRecording && sdkManager?.streamManager.isCapturing()) {
      // Stop recording
      if (sdkManager) {
        sdkManager.streamManager.stopCapture()
      }

      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop()
        setMediaRecorder(null)
      }

      if (stream) {
        stream.getTracks().forEach((track) => track.stop())
        setStream(null)
      }

      setAudioChunks([])
    }
  }, [isRecording, sdkManager, language, setAlert, stream, mediaRecorder])

  const cleanup = useCallback(() => {
    // Clean up recorder
    if (recorder) {
      recorder.stopRecording()
      setRecorder(null)
    }

    // Clean up MediaRecorder
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop()
      setMediaRecorder(null)
    }

    // Clean up SDK Manager (handles Web Audio API and WebSocket)
    if (sdkManager) {
      if (sdkManager.streamManager.isCapturing()) {
        sdkManager.streamManager.stopCapture()
      }
      sdkManager.wsClient.disconnect()
      setSdkManager(null)
    }

    // Clean up media stream
    if (stream) {
      stream.getTracks().forEach((track) => track.stop())
      setStream(null)
    }

    // Clean up URLs
    if (audioURL) {
      URL.revokeObjectURL(audioURL)
      setAudioURL('')
    }

    // Clean up responses
    setResponseSocketVoxist(null)

    // Reset recording flags
    setShouldStartRecording(false)
    setAudioChunks([])
  }, [recorder, mediaRecorder, sdkManager, stream, audioURL])

  const startRecording = async () => {
    try {
      // Clean up before starting a new recording
      cleanup()

      // Clear previous transcription
      setResponseSocketVoxist([])

      // Create SDK manager (quality enhancement disabled until validated with Banafo)
      const manager = VoxistAudioSDK.createStreamManager({
        apiBase: API_BASE,
        getToken: auth.getToken,
        language,
        model: 'banafo',
        sampleRate: 16000,
        bufferSize: 4096,
        engine: 'voxist-rt',
        enableQualityEnhancement: false,
        punctuationMode: punctuationModeRef.current
      })

      // Connect WebSocket
      await manager.wsClient.connect()
      setSdkManager(manager)
      setShouldStartRecording(true)
    } catch (error) {
      console.error('WebSocket connection error:', error)
      setAlert({ type: 'error', text: "Can't connect to transcription service" })
    }
  }

  const stopRecording = () => {
    setIsRecording(false)
  }

  const handleLanguageChange = (newLanguage: ASRLanguage) => {
    setLanguage(newLanguage)
    localStorage.setItem('selectedLanguage', newLanguage)
  }

  return [
    audioURL,
    isRecording,
    startRecording,
    stopRecording,
    language,
    handleLanguageChange,
    responseSocketVoxist,
    setResponseSocketVoxist,
    setAudioURL,
    model
  ] as const
}

export default useRecorder
